import { Component, OnInit, OnDestroy } from '@angular/core';
import { ThemeService } from 'src/app/services/theme.service';
import { ApiService } from 'src/app/services/api.service';
import { CssVar } from 'src/app/models/css-var';
import { saveAs } from 'file-saver';
import { ColorEvent, RGBA, HSLA } from 'ngx-color';

@Component({
  selector: 'app-theme-designer',
  templateUrl: './theme-designer.component.html',
  styleUrls: ['./theme-designer.component.scss'],
})
export class ThemeDesignerComponent implements OnInit, OnDestroy {
  vars: Map<string, CssVar[]>;
  channel = new BroadcastChannel('theme-designer');
  isMultiWindow: boolean;
  storageKey: string;

  constructor(
    private themeService: ThemeService,
    private apiService: ApiService
  ) {
    this.storageKey = `designer-${themeService.theme}`;

    this.channel.onmessage = (event: MessageEvent) => {
      const data: SyncAction = event.data;

      switch (data.type) {
        case 'CONNECTED':
          this.isMultiWindow = true;
          break;
        case 'DISCONNECTED':
          this.isMultiWindow = false;
          break;
        case 'RESET':
          (window as any).location.reload();
          break;
        case 'STYLE':
          this.vars = data.message;
          break;
        case 'CHANGE':
          const groupName = data.message.key.split('-', 3).pop();
          const newVar = this.vars[groupName].find((v: CssVar) => v.key === data.message.key);
          newVar.value = data.message.value;
          document.documentElement.style.setProperty(newVar.key, newVar.value);
          localStorage.setItem(this.storageKey, JSON.stringify(this.vars));
      }
    };

    this.channel.postMessage(<SyncAction>{ type: 'CONNECTED' });
  }

  ngOnInit() {
    const previousSession = localStorage.getItem(this.storageKey);

    if (previousSession) {
      this.vars = JSON.parse(previousSession);
      this.channel.postMessage(<SyncAction>{
        type: 'STYLE',
        message: this.vars,
      });

      Object.values(this.vars)
        .flatMap((v) => v)
        .filter((v) => v.value !== v.original)
        .forEach((v) => this.update(v));
    } else {
      this.apiService.getClientStyles(this.themeService.theme).subscribe((varMap) => {
        this.vars = varMap;
        this.channel.postMessage(<SyncAction>{
          type: 'STYLE',
          message: varMap,
        });
      });
    }
  }

  ngOnDestroy() {
    this.channel.close();
  }

  onEvent(_var: CssVar, event: any) {
    if (event.target.tagName === 'INPUT') {
      event.target.focus();
      _var.picker = true;

      event.target.onblur = function () {
        _var.picker = false;
      };
    }

    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  valueChange(_var: CssVar, event: Event) {
    const target = <HTMLInputElement>event.target;
    _var.value = target.value;

    this.update(_var);
  }

  focusIn(_var: CssVar) {
    if (_var.color !== 'none') {
      _var.picker = true;
    }
  }

  focusOut(_var: CssVar) {
    _var.picker = false;
  }

  onColorChange(_var: CssVar, value: ColorEvent) {
    let c: RGBA | HSLA;

    switch (_var.color) {
      case 'hex':
        _var.value = value.color.hex;
        break;
      case 'rgba':
        c = value.color.rgb;
        _var.value = `rgba(${c.r},${c.g},${c.b},${c.a})`;
        break;
      case 'hsla':
        c = value.color.hsl;
        _var.value = `hsla(${c.h},${c.s * 100}%,${c.l * 100}%,${c.a})`;
    }

    this.update(_var);
  }

  resetValue(_var: CssVar) {
    const groupName = _var.key.split('-', 3).pop();
    const originalVar = this.vars[groupName].find((v: CssVar) => v.key === _var.key);
    originalVar.value = originalVar.original;

    this.update(originalVar);
  }

  saveTheme() {
    const result: string = Object.values(this.vars)
      .flatMap((v) => v)
      .map((v) => `\t${v.key}: ${v.value}`)
      .join(';\n');

    saveAs(new Blob([`:root {\n${result}\n}`]), '_theme.css');
  }

  resetAll() {
    localStorage.removeItem(this.storageKey);
    this.channel.postMessage(<SyncAction>{ type: 'RESET' });

    (window as any).location.reload();
  }

  isGroupModified(group: any): boolean {
    return this.vars[group.key].some((v: CssVar) => v.original !== v.value);
  }

  private update(_var: CssVar) {
    document.documentElement.style.setProperty(_var.key, _var.value);

    this.channel.postMessage(<SyncAction>{
      type: 'CHANGE',
      message: _var,
    });
  }
}

export interface SyncAction {
  type: ActionType;
  message: any;
}

export type ActionType = 'CONNECTED' | 'DISCONNECTED' | 'STYLE' | 'CHANGE' | 'RESET';
